import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFetchNotifications } from './useFetchNotifications';
import { trackEvent } from '../../../modules/analytics/trackingMiddleware';
import { NOTIFICATION_OPENED, NOTIFICATION_MODULE_SELECTED } from '../../../modules/analytics/events';

interface UseNotificationModalProps {
  initialTab?: 'all' | 'unread' | 'read';
  onClose: () => void;
}

export const useNotificationModal = ({
  initialTab = 'all',
  onClose,
}: UseNotificationModalProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(initialTab);

  const {
    notifications,
    readCount,
    unreadCount,
    allCount,
    error,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    markNotification
  } = useFetchNotifications({ filter: [activeTab] });

  const handleTabChange = useCallback((newValue: 'all' | 'unread' | 'read') => {
    setActiveTab(newValue);
    trackEvent(NOTIFICATION_MODULE_SELECTED, { tab: newValue });
  }, []);

  const handleNavigate = useCallback(() => {
    trackEvent(NOTIFICATION_OPENED, { source: 'go_to_notification_center' });
    onClose();
    navigate('/notifications');
  }, [navigate, onClose]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const filteredNotifications = useMemo(() => {
    if (activeTab === 'unread') {
      return notifications.filter((n) => n.status === 'unread');
    }
    if (activeTab === 'read') {
      return notifications.filter((n) => n.status === 'read');
    }
    return notifications;
  }, [activeTab, notifications]);

  return {
    notifications: filteredNotifications,
    readCount,
    unreadCount,
    allCount,
    error,
    isError,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    activeTab,
    setActiveTab: handleTabChange,
    handleNavigateToNotifications: handleNavigate,
    handleCloseModal: handleClose,
    markNotification
  };
};
